<!--
  The scene art Webwin ships, written in its own art markup. Each <Define> is drawn by
  <Art ref="name"/> or <Use ref="name"/>, and every attribute on it is a parameter with its default.
  Nothing here is special: an application's own library is written the same way.

  Angles in the markup are degrees; `t` is seconds on the art's clock; `reveal` is the control's
  draw-in, 1 when nothing is drawing it in.
-->
<Library>

  <!-- A night sky: a gradient, and stars that twinkle and thin out toward the horizon. -->
  <Define name="starfield" label="Starfield" top="#05070d" bottom="#141a2e" starColor="#f0e6d2"
          count="110" band="0.7" twinkle="1">
    <Param name="count" label="Stars" kind="number" min="0" max="400" step="10"/>
    <Param name="twinkle" label="Twinkle" kind="number" min="0" max="1" step="0.1"/>
    <Param name="top" label="Sky (top)" kind="color"/>
    <Param name="bottom" label="Sky (bottom)" kind="color"/>
    <Param name="starColor" label="Star color" kind="color"/>
    <Param name="band" label="Reach down" kind="number" min="0.1" max="1" step="0.05"/>
    <Rect width="w" height="h" fill="linear(180, top, bottom)"/>
    <Scatter count="count" width="w" height="h * band">
      <Let name="beat" value="0.5 + 0.5 * sin(t * (0.6 + rand(3) * 2.2) + rand(4) * tau)"/>
      <Circle r="0.5 + rand(1) * rand(2) * 1.6"
              fill="alpha(starColor, (1 - twinkle * (0.65 - 0.65 * beat)) * max(0.15, 1 - v) * reveal)"/>
    </Scatter>
  </Define>

  <!-- The burning curve of a world under the sky: a rim of fire, its haze, and cracks in the crust. -->
  <Define name="horizon" label="Burning horizon" crest="0.76" fire="accent" spark="spark(fire)" rock="#0c0908"
          cracks="16" haze="1">
    <Param name="crest" label="Crest" kind="number" min="0.1" max="1" step="0.02"/>
    <Param name="fire" label="Fire" kind="color"/>
    <Param name="spark" label="Sparks" kind="color"/>
    <Param name="rock" label="Rock" kind="color"/>
    <Param name="cracks" label="Cracks" kind="number" min="0" max="40" step="1"/>
    <Param name="haze" label="Haze" kind="number" min="0" max="1" step="0.1"/>
    <Let name="R" value="max(w * 0.95, h * 1.2)"/>
    <Let name="cx" value="w / 2"/>
    <Let name="cy" value="h * crest + R"/>
    <Let name="breathe" value="0.85 + 0.15 * sin(t * 0.9)"/>
    <Group visible="haze > 0">
      <Circle cx="cx" cy="cy" r="R * 1.35"
              fill="radial(alpha(fire, 0.22 * breathe * haze * reveal), alpha(fire, 0))"/>
      <Repeat count="4">
        <Circle cx="cx" cy="cy" r="R + (4 - i) * 10" strokeWidth="11"
                stroke="alpha(fire, 0.1 * (1 - (4 - i) / 5) * breathe * haze * reveal)"/>
      </Repeat>
    </Group>
    <Circle cx="cx" cy="cy" r="R" fill="alpha(rock, reveal)"/>
    <Circle cx="cx" cy="cy" r="R - 1" strokeWidth="2.5"
            stroke="alpha(mix(fire, spark, 0.35), 0.9 * breathe * reveal)"/>
    <Circle cx="cx" cy="cy" r="R - 5" strokeWidth="6" stroke="alpha(fire, 0.18 * breathe * reveal)"/>
    <Repeat count="cracks">
      <Let name="glow" value="0.4 + 0.6 * (0.5 + 0.5 * sin(t * 1.7 + rand(7) * tau))"/>
      <Let name="steps" value="4 + floor(rand(2) * 6)"/>
      <Group x="cx" y="cy" rotate="deg((rand(1) - 0.5) * 1.3)">
        <Walk y="-R * (1 - rand(9) * 0.01)" steps="steps" dx="R * (rand(3) - 0.5) * 0.06"
              dy="R * (0.008 + rand(4) * 0.02)" strokeWidth="2"
              stroke="alpha(fire, 0.7 * glow * reveal)" glow="alpha(fire, glow * reveal)" glowSize="4"/>
        <Walk y="-R * (1 - rand(9) * 0.01)" steps="steps" dx="R * (rand(3) - 0.5) * 0.06"
              dy="R * (0.008 + rand(4) * 0.02)" strokeWidth="0.8"
              stroke="alpha(spark, 0.8 * glow * reveal)"/>
      </Group>
    </Repeat>
  </Define>

  <!-- Embers rising through the whole box, cooling as they go. -->
  <Define name="embers" label="Embers" fire="accent" spark="spark(fire)" count="80" rise="1" sway="1" emberSize="1">
    <Param name="count" label="Embers" kind="number" min="0" max="200" step="5"/>
    <Param name="fire" label="Fire" kind="color"/>
    <Param name="spark" label="Sparks" kind="color"/>
    <Param name="rise" label="Rise speed" kind="number" min="0.2" max="3" step="0.1"/>
    <Param name="sway" label="Sway" kind="number" min="0" max="3" step="0.1"/>
    <Param name="emberSize" label="Ember size" kind="number" min="0.5" max="3" step="0.1"/>
    <Repeat count="count">
      <Let name="life" value="fract(t * (0.035 + rand(2) * 0.09) * rise + rand(4))"/>
      <Let name="a" value="sin(pi * life) * (1 - life * 0.5) * reveal"/>
      <Let name="size" value="(0.8 + rand(5) * rand(6) * 2.6) * emberSize"/>
      <Group x="rand(1) * w + sin(t * 0.8 + rand(4) * 40) * (6 + rand(3) * 26) * sway + life * 30"
             y="h * (1.02 - life * 1.1)">
        <Circle r="size * 3.2" fill="alpha(fire, a * 0.12)"/>
        <Circle r="size" fill="alpha(mix(spark, fire, life), a)"/>
      </Group>
    </Repeat>
  </Define>

  <!--
    A ring that draws itself round: solid, dashed or double, with arcs racing round it and a wave
    pulsing out. `spin` and `arcSpeed` are radians a second, `arcLength` radians.
  -->
  <Define name="ring" label="Ring" color="accent" trim="#ffd36b" dashed="0" double="0" thickness="2"
          spin="0.05" arcs="0" arcSpeed="0.7" arcLength="0.5" dashes="48" pulse="0" intro="1.4">
    <Param name="color" label="Color" kind="color"/>
    <Param name="trim" label="Trim" kind="color"/>
    <Param name="dashed" label="Dashed" kind="boolean"/>
    <Param name="double" label="Double" kind="boolean"/>
    <Param name="thickness" label="Thickness" kind="number" min="0.5" max="8" step="0.5"/>
    <Param name="spin" label="Spin" kind="number" min="0" max="2" step="0.05"/>
    <Param name="arcs" label="Racing arcs" kind="number" min="0" max="8" step="1"/>
    <Param name="arcSpeed" label="Arc speed" kind="number" min="0" max="4" step="0.1"/>
    <Param name="arcLength" label="Arc length" kind="number" min="0.1" max="3" step="0.1"/>
    <Param name="dashes" label="Dashes" kind="number" min="4" max="120" step="1"/>
    <Param name="pulse" label="Pulse every" kind="number" min="0" max="10" step="0.5"/>
    <Param name="intro" label="Draw-in seconds" kind="number" min="0" max="10" step="0.2"/>
    <Let name="cx" value="w / 2"/>
    <Let name="cy" value="h / 2"/>
    <Let name="outer" value="min(w, h) / 2 - thickness / 2 - 1"/>
    <Let name="drawn" value="(intro > 0 ? smooth(t / intro) : 1) * reveal"/>
    <Let name="turn" value="deg(t * spin)"/>
    <Group visible="outer > 0 and drawn > 0.001">
      <Arc visible="!dashed" cx="cx" cy="cy" r="outer" thickness="thickness" start="turn"
           end="turn + 360 * drawn" fill="alpha(color, 0.85)"/>
      <Let name="slots" value="max(4, floor(dashes))"/>
      <Repeat visible="dashed" count="floor(slots * drawn + 0.000001)">
        <Arc cx="cx" cy="cy" r="outer" thickness="thickness" start="turn + i * 360 / slots"
             end="turn + (i + 0.55) * 360 / slots" fill="alpha(color, 0.85)"/>
      </Repeat>
      <Arc visible="double" cx="cx" cy="cy" r="outer * 0.8" thickness="thickness / 2"
           start="-turn * 2" end="-turn * 2 - 360 * drawn" fill="alpha(trim, 0.8)"/>
    </Group>
    <Let name="race" value="intro > 0 ? smooth((t - intro * 0.7) / 0.8) : 1"/>
    <Repeat count="floor(arcs)">
      <Let name="from" value="deg(t * arcSpeed + i * tau / n)"/>
      <Arc cx="cx" cy="cy" r="outer - thickness * 2.5" thickness="thickness" start="from"
           end="from + deg(arcLength)" fill="alpha(trim, 0.85 * race * reveal)"/>
    </Repeat>
    <Let name="k" value="pulse > 0 ? mod(t, pulse) / pulse : 0"/>
    <Circle visible="pulse > 0 and t > intro" cx="cx" cy="cy" r="outer * (0.4 + 0.72 * smooth(k))"
            strokeWidth="2" stroke="alpha(color, 0.4 * (1 - k) * (1 - k) * reveal)"/>
  </Define>

  <!--
    A summoning circle that draws itself: rings sweeping round, fire racing inside them, runes
    appearing one after another, and a star polygon drawn stroke by stroke.
  -->
  <Define name="sigil" label="Sigil" color="#ffd36b" fire="accent" spark="spark(fire)" points="7" step="2"
          runes="28" spin="0.05" pulse="3.4" intro="2.4" overflow="min(w, h) * 0.14">
    <Param name="color" label="Color" kind="color"/>
    <Param name="fire" label="Fire" kind="color"/>
    <Param name="spark" label="Sparks" kind="color"/>
    <Param name="points" label="Star points" kind="number" min="5" max="12" step="1"/>
    <Param name="step" label="Star step" kind="number" min="1" max="5" step="1"/>
    <Param name="runes" label="Runes" kind="number" min="0" max="48" step="1"/>
    <Param name="spin" label="Spin" kind="number" min="0" max="1" step="0.01"/>
    <Param name="pulse" label="Pulse every" kind="number" min="0" max="10" step="0.2"/>
    <Param name="intro" label="Draw-in seconds" kind="number" min="0" max="10" step="0.2"/>
    <Let name="cx" value="w / 2"/>
    <Let name="cy" value="h / 2"/>
    <Let name="ring" value="min(w, h) / 2 - 8"/>
    <Let name="dim" value="mix(color, #040508, 0.4)"/>
    <!-- The intro is the original's own seconds, scaled to however long this one takes. -->
    <Let name="at" value="intro > 0 ? t * 2.4 / intro : 99"/>
    <Let name="turn" value="deg(t * spin)"/>
    <Let name="drawn" value="smooth(at / 1.4)"/>
    <Group visible="ring > 0">
      <Circle cx="cx" cy="cy" r="ring * 1.25"
              fill="radial(alpha(fire, 0.16 * drawn * (0.85 + 0.15 * sin(t * 0.9)) * reveal), alpha(fire, 0))"/>
      <Group visible="drawn > 0.001">
        <Arc cx="cx" cy="cy" r="ring" thickness="3" start="turn" end="turn + 360 * drawn"
             fill="alpha(color, 0.75 * reveal)"/>
        <Arc cx="cx" cy="cy" r="ring + 5.5" thickness="1" start="-turn" end="-turn + 360 * drawn"
             fill="alpha(dim, 0.6 * reveal)"/>
        <Arc cx="cx" cy="cy" r="ring * 0.8" thickness="1.5" start="-turn * 2"
             end="-turn * 2 - 360 * drawn" fill="alpha(dim, 0.8 * reveal)"/>
      </Group>
      <Repeat count="3">
        <Let name="from" value="deg(t * 0.7) + i * 120"/>
        <Arc cx="cx" cy="cy" r="ring * 0.975" thickness="ring * 0.02" start="from" end="from + deg(0.5)"
             fill="alpha(fire, 0.7 * smooth((at - 1) / 0.8) * reveal)"/>
      </Repeat>
      <Let name="runesAt" value="(at - 0.8) / 1.2"/>
      <Let name="size" value="ring * 0.038"/>
      <Repeat count="runes">
        <Let name="shown" value="smooth((runesAt - i / n) * 6) * reveal"/>
        <Let name="phase" value="rand(9) * tau"/>
        <Let name="ink" value="mix(color, spark, 0.5 + 0.5 * sin(t * 1.3 + phase))"/>
        <Let name="flicker" value="0.55 + 0.45 * sin(t * 2.1 + phase)"/>
        <Group visible="shown > 0" x="cx" y="cy" rotate="i / n * 360 + turn">
          <Repeat count="2 + floor(rand(1) * 2)" as="s">
            <Let name="x1" value="(floor(rand(2) * 3) - 1) * size"/>
            <Let name="y1" value="(floor(rand(3) * 3) - 1) * size - ring * 0.89"/>
            <Let name="x2" value="(floor(rand(4) * 3) - 1) * size"/>
            <Let name="y2" value="(floor(rand(5) * 3) - 1) * size - ring * 0.89"/>
            <Line x1="x1" y1="y1" x2="x2" y2="y2" strokeWidth="3.5" stroke="alpha(fire, 0.12 * shown)"/>
            <Line x1="x1" y1="y1" x2="x2" y2="y2" strokeWidth="1.3" stroke="alpha(ink, flicker * shown)"/>
          </Repeat>
        </Group>
      </Repeat>
      <Let name="star" value="(at - 0.5) / 1.4"/>
      <Let name="spinStar" value="deg(-t * 0.08)"/>
      <Star cx="cx" cy="cy" r="ring * 0.8" points="points" step="step" turn="spinStar"
            reveal="clamp(star)" strokeWidth="6" stroke="alpha(fire, 0.1 * reveal)"/>
      <Star cx="cx" cy="cy" r="ring * 0.8" points="points" step="step" turn="spinStar"
            reveal="clamp(star)" strokeWidth="1.4" stroke="alpha(mix(fire, spark, 0.3), 0.7 * reveal)"/>
      <Let name="pips" value="smooth(star - 0.9)"/>
      <Repeat visible="pips > 0" count="points">
        <Group x="cx" y="cy" rotate="spinStar + i * 360 / n">
          <Let name="s" value="4 + sin(t * 3 + i) * 0.8"/>
          <Diamond cy="-ring * 0.8" width="s * 2" fill="alpha(color, pips * reveal)"/>
        </Group>
      </Repeat>
      <Let name="wave" value="pulse > 0 ? mod(at - 2, pulse) / pulse : 0"/>
      <Circle visible="pulse > 0 and at >= 2" cx="cx" cy="cy" r="ring * 0.4 + ring * 0.72 * smooth(wave)"
              strokeWidth="2" stroke="alpha(fire, 0.4 * (1 - wave) * (1 - wave) * reveal)"/>
    </Group>
  </Define>

  <!-- A rule that grows out from its center, fading at the ends, with a diamond set in the middle. -->
  <Define name="rule" label="Rule" color="#ffd36b" jewel="accent" thickness="1" intro="0.7">
    <Param name="color" label="Color" kind="color"/>
    <Param name="jewel" label="Jewel" kind="color"/>
    <Param name="thickness" label="Thickness" kind="number" min="0.5" max="6" step="0.5"/>
    <Param name="intro" label="Draw-in seconds" kind="number" min="0" max="5" step="0.1"/>
    <Let name="grown" value="(intro > 0 ? smooth(t / intro) : 1) * reveal"/>
    <Let name="reach" value="w / 2 * grown"/>
    <Let name="d" value="min(10, h)"/>
    <Group visible="reach > 0.5">
      <Rect x="w / 2 - reach" y="(h - thickness) / 2" width="reach" height="thickness"
            fill="linear(90, alpha(color, 0), color)"/>
      <Rect x="w / 2" y="(h - thickness) / 2" width="reach" height="thickness"
            fill="linear(90, color, alpha(color, 0))"/>
    </Group>
    <Diamond cx="w / 2" cy="h / 2" width="d" fill="alpha(color, max(grown, 0.001))"/>
    <Diamond cx="w / 2" cy="h / 2" width="d / 2" fill="alpha(jewel, grown)"/>
  </Define>

  <!-- A pool of light on the floor, seen at an angle, breathing, with a shadow at its heart. -->
  <Define name="pool" label="Pool of light" color="accent" shadow="#040508" pulse="1">
    <Param name="color" label="Color" kind="color"/>
    <Param name="shadow" label="Shadow" kind="color"/>
    <Param name="pulse" label="Breathing" kind="number" min="0" max="2" step="0.1"/>
    <Let name="breathe" value="1 - pulse * 0.25 * (0.5 + 0.5 * sin(t * 2.4))"/>
    <Ellipse cx="w / 2" cy="h / 2" rx="w / 2" ry="h / 2"
             fill="radial(alpha(color, 0.45 * breathe * reveal), alpha(color, 0))"/>
    <Ellipse cx="w / 2" cy="h / 2" rx="w * 0.15" ry="h * 0.15"
             fill="radial(alpha(shadow, 0.8 * reveal), alpha(shadow, 0))"/>
  </Define>

</Library>
